<template>
  <div class="ma-4 px-4">
    <searchBar
      @add="openCreateDialog"
      @updateSearch="(v) => (search = v)"
      headerText=""
      @fileUploaded="initialise"
    />

    <FeeScheduleTable
      :headers="feeScheduleHeaders"
      :items="feeSchedules"
      :search="search"
      :programs="programs"
      :partners="partners"
      :get-program-name="getProgramName"
      :get-partner-name="getPartnerName"
      :format-currency="formatCurrency"
      :format-date="formatDate"
      :custom-filter="customFilter"
      @edit="edit"
      @delete="deleteFeeSchedule"
    />

    <FeeScheduleForm
      :visible="dialog"
      :edit-mode="editMode"
      :fee-schedule="feeSchedule"
      :programs="programs"
      :partners="partners"
      :get-program-display-text="getProgramDisplayText"
      @update:visible="dialog = $event"
      @save="save"
      @close="closeDialog"
    />

    <ConfirmDialog
      v-if="showDeleteDialog"
      :show="showDeleteDialog"
      @delete="confirmDelete"
      @close="showDeleteDialog = false"
      title="Fee Schedule"
      :name="`Fee Schedule for Year ${currentItem?.year}`"
      :active="false"
      delete-message="Are you sure you want to delete this fee schedule?"
    />
  </div>
</template>

<script>
import searchBar from "@/components/searchBar.vue";
import ConfirmDialog from "@/components/confirm.vue";
import FeeScheduleTable from "@/components/ProgramAddons/FeeScheduleTable.vue";
import FeeScheduleForm from "@/components/ProgramAddons/FeeScheduleForm.vue";
import { feeScheduleHeaders } from "@/constants/headers";

export default {
  components: {
    searchBar,
    ConfirmDialog,
    FeeScheduleTable,
    FeeScheduleForm,
  },

  data() {
    return {
      dialog: false,
      editMode: false,
      search: "",
      programAddonId: null,
      programAddonName: "",
      feeSchedules: [],
      feeSchedule: {},
      currentItem: {},
      showDeleteDialog: false,
      programs: [],
      partners: [],
      feeScheduleHeaders: feeScheduleHeaders,
    };
  },

  created() {
    this.programAddonId = parseInt(this.$route.query.program_addon_id);
    this.programAddonName = this.$route.query.program_addon_name || "";

    this.$store.commit("setBreadcrumbs", [
      { text: "Add-on Programmes", to: { name: "Add-on Programmes" } },
      { text: this.programAddonName },
    ]);

    if (!this.programAddonId) {
      this.$store.commit("showSnackbar", {
        message: "No add-on programme selected",
        color: "error",
      });
      this.$router.push("/program_addons");
      return;
    }

    this.initialise();
    this.fetchPrograms();
    this.fetchPartners();
    
  },

  methods: {
    initialise() {
      this.getFeeSchedules();
    },

    async getFeeSchedules() {
      await this.$axios
        .get("program_addon_fee_schedule", {
          params: { program_addon_id: this.programAddonId },
        })
        .then((res) => {
          this.feeSchedules = res.data.data.sort((a, b) => b.id - a.id);
        })
        .catch(async (error) => {
          this.$store.commit("showSnackbar", {
            message:
              error.response?.data?.message || "Error fetching fee schedules",
            color: "error",
          });
        });
    },

    async fetchPrograms() {
      await this.$axios
        .get("programs")
        .then((res) => {
          this.programs = res.data.data;
        })
        .catch((error) => {
          console.error("Error fetching programs:", error);
        });
    },

    async fetchPartners() {
      await this.$axios
        .get("partners")
        .then((res) => {
          this.partners = res.data.data;
        })
        .catch((error) => {
          console.error("Error fetching partners:", error);
        });
    },

    async createFeeSchedule(data) {
      const payload = {
        program_addon_fee_schedule: {
          program_id: data.program_id,
          partner_id: data.partner_id,
          batch: data.batch,
          year: data.year,
          amount: data.amount,
          enrollment_end: data.enrollment_end,
          program_addon_id: this.programAddonId,
        },
      };

      await this.$axios
        .post("program_addon_fee_schedule", payload)
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Fee schedule created successfully",
            color: "success",
          });
          this.closeDialog();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response,
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },

    async updateFeeSchedule(data) {
      await this.$axios
        .put("program_addon_fee_schedule/" + data.id, {
          program_addon_fee_schedule: data,
        })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Fee schedule updated successfully",
            color: "success",
          });
          this.closeDialog();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response,
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },

    async deleteFeeSchedule(item) {
      this.currentItem = item;
      this.showDeleteDialog = true;
    },

    async confirmDelete() {
      await this.$axios
        .delete("program_addon_fee_schedule/" + this.currentItem.id)
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Fee schedule deleted successfully",
            color: "success",
          });
          this.showDeleteDialog = false;
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response,
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },

    openCreateDialog() {
      this.editMode = false;
      this.feeSchedule = {};
      this.dialog = true;
    },

    edit(item) {
      this.feeSchedule = { ...item };
      this.editMode = true;
      this.dialog = true;
    },

    save(data) {
      if (this.editMode) {
        this.updateFeeSchedule(data);
      } else {
        this.createFeeSchedule(data);
      }
    },

    closeDialog() {
      this.dialog = false;
      this.editMode = false;
      this.feeSchedule = {};
    },

    formatCurrency(amount) {
      if (!amount) return "0.00";
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
      }).format(amount);
    },

    formatDate(date) {
      if (!date) return "";

      const d = new Date(date);

      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();

      return `${day}-${month}-${year}`;
    },

    getProgramDisplayText(program) {
      return program
        ? program.degree.short_name +
            (program.specialization ? " - " + program.specialization : "")
        : "";
    },

    getProgramName(ids) {
      if (!Array.isArray(ids) || ids.length === 0) {
        return "-";
      }

      return this.programs
        .filter((program) => ids.includes(program.id))
        .map((program) => {
          return (
            program.degree.short_name +
            (program.specialization ? ` - ${program.specialization}` : "")
          );
        })
        .join(", ");
    },

    getPartnerName(ids) {
      if (!Array.isArray(ids) || ids.length === 0) {
        return "-";
      }

      return this.partners
        .filter((partner) => ids.includes(partner.id))
        .map((partner) => partner.name)
        .join(", ");
    },

    customFilter(value, search, item) {
      if (!search) return true;
      return (
        item.program_id
          ?.toString()
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        item.partner_id
          ?.toString()
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        item.batch?.toString().toLowerCase().includes(search.toLowerCase()) ||
        item.year?.toString().toLowerCase().includes(search.toLowerCase()) ||
        item.amount?.toString().toLowerCase().includes(search.toLowerCase()) ||
        this.getProgramName(item.program_id)
          ?.toLowerCase()
          .includes(search.toLowerCase()) ||
        this.getPartnerName(item.partner_id)
          ?.toLowerCase()
          .includes(search.toLowerCase())
      );
    },
  },
};
</script>

<style scoped>
.description-preview {
  line-height: 1.6;
  font-size: 14px;
}

.description-preview >>> p {
  margin-bottom: 10px;
}

.description-preview >>> ul,
.description-preview >>> ol {
  margin-left: 20px;
  margin-bottom: 10px;
}

.description-preview >>> strong,
.description-preview >>> b {
  font-weight: bold;
}

.description-preview >>> em,
.description-preview >>> i {
  font-style: italic;
}

.description-preview >>> a {
  color: #4f4584;
  text-decoration: underline;
}
</style>
